import styled from '@emotion/styled';
import { Checkbox, Result, Table } from 'antd';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { getAllProjectsStateType } from 'components/organism/PricingSimulator/Projects';
import { colorConstants } from 'config/colorConstants';
import { pricingSimulatorUrls } from 'config/urls';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { useFetchGetAllProjects } from 'hooks/pricingSimulator/useFetchGetAllProjects';
import { isNil } from 'lodash';
import { getAllScenarioParser } from 'parser/pricingSimulator/ProjectsScenariosParser';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { queryClient } from 'services/reactQueryService';
import tw from 'twin.macro';
import { pricingSimulatorIndexTableColumns } from 'utils/getPricingSimulatorTableColumns';
import { getConfig } from 'utils/helperMethods';
export const CustomTable = styled(Table)`
    // .ant-table-sticky-scroll {
    //     position: static;
    // }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        //padding: 0px;
        // height: 68px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    // border: ${'1px solid #DBDFE9'};
`;

type Props = {
    tableProjectsReactQueryVariables: getAllProjectsStateType;
    setTableProjectsReactQueryVariables: Function;
    onEdit: (projectData: any) => void;
    newlyCreatedProjectId: number | null;
    setNewlyCreatedProjectId: Function;
};

const projectsTableId = 'projects-index-table';

const PricingSimulatorIndexTableWrapper: FC<Props> = ({
    tableProjectsReactQueryVariables,
    setTableProjectsReactQueryVariables,
    onEdit,
    newlyCreatedProjectId,
    setNewlyCreatedProjectId,
}) => {
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const indexTableFixedColumns = pricingSimulatorConfig.indexTableFixedColumns;
    const projectStatuses = pricingSimulatorConfig.projectStatuses;
    const enablePrivatePublicToggle = pricingSimulatorConfig.enablePrivatePublicToggle;
    const [allScenariosTableVariables, setAllScenariosTableVariables] = useState<any>();

    //simulator wrapper context
    const { dimensionConfigData, loading: configLoading } = useContext(pricingSimulatorWrapperDataContext);

    const dim1Attribute = dimensionConfigData?.dim1Attribute;
    const dim2Attribute = dimensionConfigData?.dim2Attribute;

    //filter context
    const { allDimAndAttributeTypeList, timePeriodSelection, timePeriodLoading } = useContext(FilterContextData);

    const [expandedKeys, setExpandedKeys] = useState<any>({});
    const location = useLocation();

    const mainSectionHeight = useRef<number | null>(0);

    //this hook with give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: projectsTableId,
        height: mainSectionHeight.current,
        buffer: 80,
        // triggerDummyRender: true,
    });

    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !timePeriodLoading &&
            timePeriodSelection,
    );

    //set expanded keys to default state when user searches
    useEffect(() => {
        setExpandedKeys({});
    }, [tableProjectsReactQueryVariables?.search_keyword]);

    //remove all projects and all scenarios query from cache when user leaves index page so that on revisit fresh query is called to get latest data
    useEffect(() => {
        return () => {
            queryClient.removeQueries({
                exact: false,
                queryKey: ['ps-index-table-projects'],
            });
        };
    }, []);

    //reload scenario api
    const { mutate: reloadScenarioMutation, loading: reloadScenarioLoading } = useCommonMutationHandler({
        url: pricingSimulatorUrls.reloadScenarioUrl,
        method: 'post',
    });

    //get all projects
    const {
        data: allProjectsData,
        loading: allProjectsDataLoading,
        error: allProjectsDataError,
        count: allProjectsDataCount,
        setData: setAllProjectsData,
    } = useFetchGetAllProjects({
        method: 'post',
        queryConfig: {
            enabled: Boolean(dim1Attribute) && Boolean(enabledVariable) && Boolean(tableProjectsReactQueryVariables),
        },
        queryKey: ['ps-index-table-projects', tableProjectsReactQueryVariables],
        url: `${pricingSimulatorUrls.getAllProjectsUrl}`,
        variables: {
            ...tableProjectsReactQueryVariables,
        },
        extras: { newlyCreatedProjectId, setNewlyCreatedProjectId, setExpandedKeys, setAllScenariosTableVariables },
    });

    //get all scenarios for a particular project id
    const {} = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(allScenariosTableVariables?.projectId) },
        queryKey: ['ps-index-table-projects', 'ps-index-table-scenarios', allScenariosTableVariables],
        // if my scenarios enabled add the query string to the url
        url: `${pricingSimulatorUrls.getAllScenariosUrl}/${allScenariosTableVariables?.projectId}/scenarios${
            tableProjectsReactQueryVariables.user_id && tableProjectsReactQueryVariables.project_scope === 'self'
                ? '?user_id=' + tableProjectsReactQueryVariables.user_id
                : ''
        }`,
        variables: {
            ...allScenariosTableVariables,
        },
        parserFunction: (res: any) => {
            //if error, remove loader and add error message
            if (res?.error) {
                const updatedData = allProjectsData?.map((project: any) => {
                    //find the project
                    if (project?.projectId === allScenariosTableVariables?.projectId) {
                        return {
                            ...project,
                            children: [{ isChild: true, errorMessage: 'No Data' }],
                        };
                    }
                    return {
                        ...project,
                    };
                });
                setAllProjectsData(updatedData);
                return {};
            }
            try {
                if (res?.length === 0) {
                    throw 'No Data';
                }
                //scenarios data is added in all projects data inside children key as its nested
                const updatedData = getAllScenarioParser(res, allProjectsData);
                if (updatedData?.length) setAllProjectsData(updatedData);
                return {};
            } catch (error) {
                throw error;
            }
        },
    });

    const commonLoading = timePeriodLoading || allProjectsDataLoading || configLoading;

    const memoisedColumns = useMemo(() => {
        let lastColumns = [dim1Attribute, dim2Attribute, { label: '', key: 'edit' }];
        lastColumns = lastColumns.filter((item) => !isNil(item));
        return pricingSimulatorIndexTableColumns(
            indexTableFixedColumns,
            lastColumns,
            projectStatuses,
            location.pathname,
            editClickHandler,
            {
                sortBy: tableProjectsReactQueryVariables?.sort_by || 0,
                sortOrder:
                    tableProjectsReactQueryVariables?.sorting_order === 'asc'
                        ? 'ascend'
                        : tableProjectsReactQueryVariables?.sorting_order === 'desc'
                        ? 'descend'
                        : null,
            },
            refreshClickHandler,
            enablePrivatePublicToggle,
        );
    }, [allProjectsData]);

    function infiniteScrollOnNext(loading: boolean) {
        if (!loading) {
            const newOffset = tableProjectsReactQueryVariables.offset + tableProjectsReactQueryVariables.limit;
            setTableProjectsReactQueryVariables({ ...tableProjectsReactQueryVariables, offset: newOffset });
        }
    }

    function editClickHandler(rowData: any) {
        onEdit(rowData);
    }

    //called when clicked on refresh icon
    function refreshClickHandler(scenarioId: number, projectId: number) {
        const payload = {
            scenario_id: scenarioId,
            project_id: projectId,
            user_name: localStorage.getItem('email'),
        };
        const reloadedProject = allProjectsData?.map((project: any) => {
            if (project?.projectId === projectId) {
                return {
                    ...project,
                    children: [{ isChild: true, isScenarioLoading: true }],
                };
            }
            return { ...project };
        });
        setAllProjectsData(reloadedProject);
        reloadScenarioMutation(
            { ...payload },
            {
                onSuccess: (data) => {
                    queryClient.refetchQueries({
                        exact: true,
                        queryKey: ['ps-index-table-projects', 'ps-index-table-scenarios', { projectId }],
                    });
                },
                onError: (err: any) => {
                    queryClient.refetchQueries({
                        exact: true,
                        queryKey: ['ps-index-table-projects', 'ps-index-table-scenarios', { projectId }],
                    });
                },
            },
        );
    }

    return (
        <div>
            <InfiniteScroller
                data={allProjectsData}
                loading={commonLoading}
                hasMore={allProjectsData?.length < allProjectsDataCount || commonLoading}
                height={allProjectsData && !allProjectsDataError ? mainSectionHeight.current ?? undefined : 'auto'}
                //height={200}
                //     loader={true}
                onNext={() => infiniteScrollOnNext(commonLoading)}
            >
                <CustomTable
                    onChange={(pagination, filter, sort: any, extra) => {
                        if (sort?.order) {
                            const sortMapping: any = {
                                descend: 'desc',
                                ascend: 'asc',
                            };

                            setTableProjectsReactQueryVariables({
                                ...tableProjectsReactQueryVariables,
                                sort_by: sort?.column?.sortKey,
                                sorting_order: sortMapping?.[sort?.order] || 'desc',
                                offset: 0,
                            });
                            //default it to dim1 column on cancelling sorting
                        } else {
                            setTableProjectsReactQueryVariables({
                                ...tableProjectsReactQueryVariables,
                                sort_by: 'updated_at',
                                sorting_order: 'desc',
                                offset: 0,
                            });
                        }
                    }}
                    sticky={{
                        getContainer: () =>
                            document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                    }} //adding x axis scrollbar on table
                    id={projectsTableId}
                    //loading={commonLoading}
                    columns={memoisedColumns}
                    scroll={{ x: '100%' }}
                    dataSource={allProjectsData}
                    pagination={false}
                    style={{
                        opacity: commonLoading ? '0.3' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: allProjectsDataError ? <ErrorBox msg={String(allProjectsDataError)} /> : '',
                    }}
                    rowClassName={(record: any) => {
                        let className = '';

                        //if expired, then disabled row
                        if (record?.status === 'expired') {
                            className += 'opacity-60 cursor-not-allowed pointer-events-none';
                        } else {
                            if (record?.key in expandedKeys) className += ' bg-backgroundGreyAlt';
                            if (record && 'children' in record) className += ' cursor-pointer';
                        }
                        return className;
                    }}
                    expandable={{
                        expandedRowKeys: Object.keys(expandedKeys),
                        expandRowByClick: true,
                        onExpand(expanded, record: any) {
                            if (reloadScenarioLoading) return;
                            //if false, collapse table row
                            if (!expanded) {
                                //temp change
                                //removing data from cache so that api call will be triggered
                                queryClient.removeQueries({
                                    queryKey: [
                                        'ps-index-table-projects',
                                        'ps-index-table-scenarios',
                                        allScenariosTableVariables,
                                    ],
                                });
                                const tempExpandedItems = { ...expandedKeys };
                                delete tempExpandedItems?.[record?.key];
                                setExpandedKeys(tempExpandedItems);
                                setAllScenariosTableVariables({});
                                //if true, expand table row
                            } else if (record && 'children' in record) {
                                setAllScenariosTableVariables({ projectId: record?.projectId });
                                setExpandedKeys({ ...expandedKeys, [record?.key]: true });
                            }
                        },
                    }}
                />
            </InfiniteScroller>
        </div>
    );
};

export default PricingSimulatorIndexTableWrapper;
